<template>
	<v-container fluid>
		<v-row>
			<v-col cols="12" lg="12" class="pb-0 pt-0">
				<v-card flat>
					<v-card-text class="pb-0 pt-2">
						<v-row class="d-flex justify-space-between pb-1">
							<v-col class="d-flex pa-0 pl-2">
								<v-btn color="primary" @click="addExternalCertificate">
									<v-icon small>fa-plus</v-icon>&nbsp;&nbsp;{{ $t('buttons.add') }}
								</v-btn>
								<v-btn color="primary" class="ml-2" @click="issueCertificates">
									<v-icon small>fa-plus</v-icon>&nbsp;&nbsp;{{ $t('buttons.issueCertificates') }}
								</v-btn>
							</v-col>
							<v-col class="d-flex pb-0 pt-0 d-flex justify-center">
								<v-card-title class="pa-2 pb-2 pt-0">
									{{ $t('modules.issuedCertificates') }}</v-card-title
								>
							</v-col>
							<v-col> </v-col>
						</v-row>
						<v-row>
							<v-col class="pb-0 pt-0">
								<TheTable
									:additional-filters="issuedCertificatesTable.additionalFilters"
									:default-sort="issuedCertificatesTable.defaultSort"
									:excel="true"
									:fields="issuedCertificatesTable.headers"
									:header-selection-key="'issued-certificates-headers'"
									:height="issuedCertificatesTable.height"
									:filter-save-key="'issued-certificates-filters'"
									:total-records="issuedCertificatesTable.total"
									@excel="excelExport"
									@headers="selectHeaders"
									@reload="reloadData"
								>
									<tr
										v-for="(item, key) in issuedCertificatesTable.items"
										:key="key"
										:class="{ 'cursor-pointer': true }"
									>
										<td v-if="issuedCertificatesTable.selectedHeaders.includes('id')">
											{{ item.id }}
										</td>
										<td
											v-if="
												issuedCertificatesTable.selectedHeaders.includes('certificate_number')
											"
										>
											{{ item.certificate_number }}
										</td>
										<td v-if="issuedCertificatesTable.selectedHeaders.includes('candidate_id')">
											<b>{{ item.candidate.first_name + ' ' + item.candidate.last_name }}</b>
										</td>
										<td v-if="issuedCertificatesTable.selectedHeaders.includes('birth_date')">
											{{
												item.candidate.date_of_birth != null
													? item.candidate.date_of_birth.split('-').reverse().join('.')
													: ''
											}}
										</td>
										<td v-if="issuedCertificatesTable.selectedHeaders.includes('certificate_id')">
											<b>{{ item.certificate_id == null ? '-' : item.certificate.name }}</b>
										</td>
										<td v-if="issuedCertificatesTable.selectedHeaders.includes('course_id')">
											<b>{{ item.course_id == null ? '-' : item.course.name }}</b>
										</td>
										<td
											class="truncate-course-name"
											v-if="issuedCertificatesTable.selectedHeaders.includes('planned_course_id')"
										>
											<v-tooltip bottom>
												<template v-slot:activator="{ on, attrs }">
													<b v-bind="attrs" v-on="on">{{
														item.planned_course_id == null
															? '-'
															: item.planned_course.course.code +
															  ' ' +
															  item.planned_course.course.name +
															  ' - ' +
															  item.planned_course.start_date
																	.split('-')
																	.reverse()
																	.join('.')
													}}</b>
												</template>
												<span>{{
													item.planned_course_id == null
														? '-'
														: item.planned_course.course.code +
														  ' ' +
														  item.planned_course.course.name +
														  ' - ' +
														  item.planned_course.start_date.split('-').reverse().join('.')
												}}</span>
											</v-tooltip>
										</td>
										<td v-if="issuedCertificatesTable.selectedHeaders.includes('office_id')">
											{{ item.office_id == null ? '-' : item.office.name }}
										</td>
										<td v-if="issuedCertificatesTable.selectedHeaders.includes('issue_date')">
											{{
												item.issue_date != null
													? item.issue_date.split('-').reverse().join('.')
													: ''
											}}
										</td>
										<td v-if="issuedCertificatesTable.selectedHeaders.includes('expiration_date')">
											{{
												item.expiration_date != null
													? item.expiration_date.split('-').reverse().join('.')
													: ''
											}}
										</td>
										<td>
											<v-tooltip bottom v-if="!item.course_id">
												<template v-slot:activator="{ on, attrs }">
													<v-btn
														icon
														small
														v-bind="attrs"
														@click="getCertificatePdf(item.id)"
														v-on="on"
													>
														<v-icon small color="#ff0000">fas fa-file-pdf</v-icon>
													</v-btn>
												</template>
												<span>{{ $t('tooltips.getCertificatePdf') }}</span>
											</v-tooltip>
											<v-tooltip bottom v-if="!item.certificate_file.upload_id">
												<template v-slot:activator="{ on, attrs }">
													<v-btn
														icon
														small
														v-bind="attrs"
														@click="uploadCertificate(item.certificate_number, item.id)"
														v-on="on"
													>
														<v-icon small color="#5c5c5c">fas fa-upload</v-icon>
													</v-btn>
												</template>
												<span>{{ $t('tooltips.uploadFile') }}</span>
											</v-tooltip>
											<v-tooltip bottom v-if="item.certificate_file.upload_id">
												<template v-slot:activator="{ on, attrs }">
													<v-btn
														icon
														small
														v-bind="attrs"
														@click="downloadCertificate(item.certificate_file)"
														v-on="on"
													>
														<v-icon small color="#003a9e">fas fa-download</v-icon>
													</v-btn>
												</template>
												<span>{{ $t('tooltips.downloadFile') }}</span>
											</v-tooltip>
											<v-tooltip bottom v-if="item.certificate_file.upload_id">
												<template v-slot:activator="{ on, attrs }">
													<v-btn
														icon
														small
														v-bind="attrs"
														@click="editFile(item.id, item.course_id)"
														v-on="on"
													>
														<v-icon small color="#5c5c5c">fas fa-edit</v-icon>
													</v-btn>
												</template>
												<span>{{ $t('tooltips.edit') }}</span>
											</v-tooltip>
											<v-tooltip bottom v-if="$myStore.state.user.user_role_id == 1">
												<template v-slot:activator="{ on, attrs }">
													<v-btn
														icon
														small
														v-bind="attrs"
														@click="deleteIssuedCertificate(item.id)"
														v-on="on"
													>
														<v-icon small>fas fa-trash</v-icon>
													</v-btn>
												</template>
												<span>{{ $t('tooltips.deleteIssuedCertificate') }}</span>
											</v-tooltip>
										</td>
									</tr>
								</TheTable>
							</v-col>
						</v-row>
						<v-row v-if="showModal">
							<v-col>
								<Modal :show-modal="showModal" maxWidth="1000">
									<IssuedCertificate @close="closeModal"></IssuedCertificate>
								</Modal>
							</v-col>
						</v-row>
						<v-row v-if="showExternalCertModal">
							<v-col>
								<Modal :show-modal="showExternalCertModal" maxWidth="800">
									<ExternalCertificate
										:certificateId="certificateId"
										:inEditMode="inEditMode"
										@close="closeModal"
									></ExternalCertificate>
								</Modal>
							</v-col>
						</v-row>
						<v-row v-if="showUploadModal">
							<v-col>
								<Modal :show-modal="showUploadModal" maxWidth="800">
									<UploadCertificate
										@close="closeModal"
										:certificateNumber="certificateNumber"
										:certificateId="certificateId"
										:inEditMode="inEditMode"
									></UploadCertificate>
								</Modal>
							</v-col>
						</v-row>
					</v-card-text>
				</v-card>
			</v-col>
		</v-row>
	</v-container>
</template>

<script>
import TheTable from '../../trinkets/TheTable';
import Modal from '../../trinkets/Modal';
import IssuedCertificate from './IssuedCertificate';
import UploadCertificate from './UploadCertificate';
import ExternalCertificate from './ExternalCertificate';

export default {
	name: 'IssuedCertificates',
	components: { TheTable, Modal, IssuedCertificate, UploadCertificate, ExternalCertificate },
	data: function () {
		return {
			issuedCertificatesTable: {
				selectedHeaders: [],
				headers: [
					{
						text: this.$t('fields.id'),
						value: 'id',
						type: 'string',
						allowFilter: true,
					},
					{
						text: this.$t('fields.certificateNumber'),
						value: 'certificate_number',
						type: 'string',
						allowFilter: true,
					},
					{
						text: this.$t('fields.candidate'),
						value: 'candidate_id',
						type: 'dropdown|candidates',
						allowFilter: true,
					},
					{
						text: this.$t('fields.dateOfBirth'),
						value: 'birth_date',
						type: '',
						allowFilter: true,
					},
					{
						text: this.$t('fields.certificate'),
						value: 'certificate_id',
						type: 'dropdown|certificates',
						allowFilter: true,
					},
					{
						text: this.$t('fields.course'),
						value: 'course_id',
						type: 'dropdown|courses',
						allowFilter: true,
					},
					{
						text: this.$t('fields.plannedCourse'),
						value: 'planned_course_id',
						type: 'dropdown|planned-courses',
						allowFilter: true,
					},
					{
						text: this.$t('fields.office'),
						value: 'office_id',
						type: 'dropdown|offices',
						allowFilter: true,
					},
					{
						text: this.$t('fields.issueDate'),
						value: 'issue_date',
						type: '',
						allowFilter: true,
					},
					{
						text: this.$t('fields.expirationDate'),
						value: 'expiration_date',
						type: '',
						allowFilter: true,
					},
					{ text: '', value: 'actions', type: '', sortable: false, width: '100px', allowFilter: false },
				],
				additionalFilters: [
					{
						text: this.$t('fields.dateOfBirth'),
						value: 'birth_date',
						type: 'date_range',
					},
					{
						text: this.$t('fields.issueDate'),
						value: 'issue_date',
						type: 'date_range',
					},
					{
						text: this.$t('fields.expirationDate'),
						value: 'expiration_date',
						type: 'date_range',
					},
				],
				items: [],
				total: 0,
				lastRequest: null,
				height: '0px',
				defaultSort: {
					field: 'id',
					descending: true,
				},
			},
			showModal: false,
			showUploadModal: false,
			showExternalCertModal: false,
			certificateNumber: '',
			certificateId: null,
			inEditMode: false,
		};
	},
	methods: {
		selectHeaders(headers) {
			this.issuedCertificatesTable.selectedHeaders = headers;
		},
		issueCertificates() {
			this.showModal = true;
		},
		addExternalCertificate() {
			this.showExternalCertModal = true;
		},
		uploadCertificate(number, id) {
			this.certificateNumber = number;
			this.certificateId = id;
			this.showUploadModal = true;
		},
		downloadCertificate(file) {
			window.open(file.link);
		},
		editFile(id, course_id) {
			this.certificateId = id;
			this.inEditMode = true;
			if (course_id) {
				//open external certificate
				this.showExternalCertModal = true;
			} else {
				this.showUploadModal = true;
			}
		},
		closeModal() {
			this.showModal = false;
			this.showUploadModal = false;
			this.showExternalCertModal = false;
			this.inEditMode = false;
			this.certificateId = null;
			this.certificateNumber = '';
		},
		getCertificatePdf(id) {
			let vm = this;
			this.$APICall2('get', 'issued-certificates/pdf/' + id, {}, (data, success) => {
				if (success) {
					const blob = new Blob([data], { type: 'application/pdf' });
					const url = URL.createObjectURL(blob);
					const link = document.createElement('a');
					link.href = url;
					link.download = 'certifikat.pdf';
					this.downloadingPdf = false;
					link.click();
					URL.revokeObjectURL(url);
				} else {
					vm.$alert(vm.$t('alerts.basicError'));
				}
			});
		},
		deleteIssuedCertificate(id) {
			let vm = this;
			this.$verify(this.$t('alerts.deletionQuestion'), (result) => {
				if (result) {
					this.$APICall('delete', 'issued-certificates/' + id + '/delete', {}, (data, success) => {
						if (success) {
							vm.$notify(vm.$t('alerts.basicDelete'));
							vm.reloadData(vm.issuedCertificatesTable.lastRequest);
						} else {
							vm.$alert(vm.$t('alerts.basicError'));
						}
					});
				}
			});
		},
		reloadData(request) {
			this.issuedCertificatesTable.lastRequest = request;
			let vm = this;
			this.$APICall(
				'get',
				'issued-certificates',
				request,
				(data, success) => {
					if (success) {
						vm.issuedCertificatesTable.items = data.items;
						vm.issuedCertificatesTable.total = data.total;
					} else {
						vm.$alert(vm.$t('alerts.basicError'));
					}
				},
				false
			);
		},
		excelExport() {
			let excelRequest = JSON.parse(JSON.stringify(this.issuedCertificatesTable.lastRequest));
			excelRequest.pagination.itemsPerPage = 999999999;
			excelRequest.pagination.page = 1;

			let vm = this;
			this.$APICall(
				'get',
				'issued-certificates',
				excelRequest,
				(data, success) => {
					if (success) {
						vm.$APICall('post', 'excel/issued-certificates', data.items, (data, success) => {
							window.open(data.link);
						});
					} else {
						vm.$alert(vm.$t('alerts.basicError'));
					}
				},
				false
			);
		},
		resolveWindowResize() {
			this.issuedCertificatesTable.height = window.innerHeight - 243 + 'px';
		},
	},
	mounted() {
		let vm = this;
		if (!this.$myStore.state.loggedIn) {
			this.$router.replace('/login');
		}

		this.$myStore.commit('setBreadcrumbs', [
			{ text: this.$t('modules.issuedCertificates'), href: '/#/issued-certificates', disabled: true },
		]);

		this.$modalBus.$on('reloadIssuedCertificates', () => {
			vm.reloadData(vm.issuedCertificatesTable.lastRequest);
		});

		window.addEventListener('resize', this.resolveWindowResize);
		this.resolveWindowResize();
	},
};
</script>

<style scoped>
.truncate-course-name {
	max-width: 600px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
</style>
